import { supabase } from './supabase';

// Generic helper for error handling
const handleSupabaseError = (error, defaultMessage = 'An error occurred') => {
  console.error('Supabase error:', error);
  return {
    error: error?.message || defaultMessage,
    data: null
  };
};

/* -------------------------------------------------------------------------- */
/*                         PAYMENT SETTINGS                                   */
/* -------------------------------------------------------------------------- */

// Get branch payment settings (Stripe publishable key)
export const getBranchPaymentSettings = async (branchId) => {
  try {
    const { data, error } = await supabase
      .from('payment')
      .select('stripe_key, payment_type')
      .eq('branch_id', branchId)
      .single();

    if (error) {
      // No payment settings for this branch - not necessarily an error
      return { data: null, error: null };
    }

    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         PAYMENT & ORDERS (Edge Functions)                 */
/* -------------------------------------------------------------------------- */

// Create payment intent for donation (calls Supabase Edge Function)
// Edge function handles donor creation and stores order data in metadata for webhook
export const createDonationPaymentIntent = async ({
  donorObject,
  amount,
  orderDetails,
  subtotal,
  transactionFees,
  giftAidEligible,
  giftAidData,
  isRecurring
}) => {
  try {
    const parsedDonor = typeof donorObject === 'string' ? JSON.parse(donorObject) : donorObject;

    // Call edge function which creates donor and payment intent
    const { data, error } = await supabase.functions.invoke('create-payment-intent', {
      body: {
        donorObject: parsedDonor,
        amount,
        orderDetails,
        subtotal,
        transactionFees,
        giftAidEligible,
        giftAidData,
        isRecurring
      }
    });

    console.log('firing request')
    if (error) {
      console.error('Edge function error:', error);
      return {
        data: null,
        error: error.message || 'Failed to create payment intent'
      };
    }

    if (data?.error) {
      return {
        data: null,
        error: data.error
      };
    }

    return {
      data: {
        donor_id: data.data?.donor_id,
        client_secret: data.data?.client_secret,
        payment_intent_id: data.data?.payment_intent_id,
        publishable_key: data.data?.publishable_key,
        subscription_id: data.data?.subscription_id
      },
      error: null
    };
  } catch (error) {
    console.error('Create payment intent error:', error);
    return {
      data: null,
      error: error.message || 'Failed to create payment intent'
    };
  }
};

/* -------------------------------------------------------------------------- */
/*                              BRANCHES                                      */
/* -------------------------------------------------------------------------- */

// Get all branches for an organization
export const getBranches = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single branch by ID
export const getBranch = async id => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create branch
export const createBranch = async branchData => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .insert([branchData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update branch
export const updateBranch = async (id, branchData) => {
  try {
    const { data, error } = await supabase
      .from('branches')
      .update(branchData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete branch
export const deleteBranch = async id => {
  try {
    // First, get the branch to retrieve the logo URL
    const { data: branch, error: fetchError } = await supabase
      .from('branches')
      .select('logo')
      .eq('id', id)
      .single();

    if (fetchError) return handleSupabaseError(fetchError);

    // Delete the branch from database
    const { error } = await supabase.from('branches').delete().eq('id', id);

    if (error) return handleSupabaseError(error);

    // Delete the logo from storage if it exists and is a Supabase URL
    if (branch?.logo && branch.logo.includes('supabase')) {
      try {
        const urlParts = branch.logo.split(
          '/storage/v1/object/public/uploads/'
        );
        if (urlParts.length >= 2) {
          const path = urlParts[1];
          await supabase.storage.from('uploads').remove([path]);
          console.log('Branch logo deleted from storage');
        }
      } catch (storageError) {
        console.error('Failed to delete logo from storage:', storageError);
        // Don't fail the whole operation if logo deletion fails
      }
    }

    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              DONORS                                        */
/* -------------------------------------------------------------------------- */

// Get donors with optional filters and total donation amounts
export const getDonors = async (organisationId, branchId, filters = {}) => {
  try {
    // First, get the donors
    let donorQuery = supabase
      .from('donars')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      donorQuery = donorQuery.eq('branch_id', branchId);
    }

    // Apply optional filters
    if (filters.simpleIds && filters.simpleIds.length > 0) {
      donorQuery = donorQuery.in('simple_id', filters.simpleIds);
    }

    if (filters.startDate && filters.endDate) {
      donorQuery = donorQuery
        .gte('created_at', filters.startDate)
        .lte('created_at', filters.endDate);
    }

    const { data: donors, error: donorError } = await donorQuery.order(
      'created_at',
      {
        ascending: false
      }
    );

    if (donorError) return handleSupabaseError(donorError);

    // For each donor, get their total donation amount
    const donorsWithTotals = await Promise.all(
      donors.map(async donor => {
        let orderQuery = supabase
          .from('orders')
          .select('donation_amount')
          .eq('donar_id', donor.id)
          .eq('organisation_id', organisationId);

        if (branchId) {
          orderQuery = orderQuery.eq('branch_id', branchId);
        }

        const { data: orders, error: orderError } = await orderQuery;

        if (orderError) {
          console.error('Error fetching orders for donor:', orderError);
          return { ...donor, total_donation: 0 };
        }

        const totalDonation = orders?.reduce(
          (sum, order) => sum + (order.donation_amount || 0),
          0
        );

        return {
          ...donor,
          total_donation: totalDonation || 0
        };
      })
    );

    return { data: donorsWithTotals, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single donor by ID
export const getDonor = async id => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor
export const createDonor = async donorData => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .insert([donorData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor
export const updateDonor = async (id, donorData) => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .update(donorData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor
export const deleteDonor = async id => {
  try {
    const { error } = await supabase.from('donars').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donor notes/feedback count
export const getDonorNotesCount = async donorId => {
  try {
    const { count, error } = await supabase
      .from('donars_feedback')
      .select('*', { count: 'exact', head: true })
      .eq('donar_id', donorId);

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR INSIGHTS/STATS                             */
/* -------------------------------------------------------------------------- */

// Get donor insights (all stats in one query for efficiency)
export const getDonorInsights = async (donorId, branchId) => {
  try {
    // Get all paid orders for this donor
    let ordersQuery = supabase
      .from('orders')
      .select(
        `
        id,
        donation_amount,
        created_at,
        order_details (
          amount,
          donation_type:donation_type_id (
            donation_type_name
          )
        )
      `
      )
      .eq('donar_id', donorId)
      .eq('payment_status', 'paid')
      .order('created_at', { ascending: true });

    if (branchId) {
      ordersQuery = ordersQuery.eq('branch_id', branchId);
    }

    const { data: orders, error: ordersError } = await ordersQuery;

    if (ordersError) return handleSupabaseError(ordersError);

    // Get donor creation date
    const { data: donor, error: donorError } = await supabase
      .from('donars')
      .select('created_at')
      .eq('id', donorId)
      .single();

    if (donorError) return handleSupabaseError(donorError);

    // Calculate all stats from the orders
    const stats = {
      lifetimeContribution: 0,
      ytdContribution: 0,
      firstDonation: null,
      mostRecentDonation: null,
      averageDonation: 0,
      lastDonationDate: null,
      donorCreatedAt: donor.created_at,
      totalDonations: orders?.length || 0,
      yearlyDonations: {},
      categories: {}
    };

    if (orders && orders.length > 0) {
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1).toISOString();

      orders.forEach(order => {
        const amount = order.donation_amount || 0;
        const orderYear = new Date(order.created_at).getFullYear();

        // Lifetime contribution
        stats.lifetimeContribution += amount;

        // YTD contribution
        if (order.created_at >= yearStart) {
          stats.ytdContribution += amount;
        }

        // Yearly donations
        if (!stats.yearlyDonations[orderYear]) {
          stats.yearlyDonations[orderYear] = 0;
        }
        stats.yearlyDonations[orderYear] += amount;

        // Categories
        if (order.order_details && order.order_details.length > 0) {
          order.order_details.forEach(detail => {
            const categoryName =
              detail.donation_type?.donation_type_name || 'General Donation';
            const detailAmount = detail.amount || 0;
            stats.categories[categoryName] =
              (stats.categories[categoryName] || 0) + detailAmount;
          });
        } else if (amount > 0) {
          stats.categories['General Donation'] =
            (stats.categories['General Donation'] || 0) + amount;
        }
      });

      // First and most recent donations
      stats.firstDonation = orders[0].donation_amount;
      stats.mostRecentDonation = orders[orders.length - 1].donation_amount;
      stats.lastDonationDate = orders[orders.length - 1].created_at;

      // Average donation
      stats.averageDonation = stats.lifetimeContribution / orders.length;
    }

    return { data: stats, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR ORDERS                                     */
/* -------------------------------------------------------------------------- */

// Get all orders for a specific donor
export const getDonorOrders = async (donorId, branchId) => {
  try {
    let ordersQuery = supabase
      .from('orders')
      .select(
        `
        id,
        simple_id,
        gift_aid_eligible,
        payment_status,
        donation_amount,
        subtotal,
        transaction_fees,
        created_at,
        updated_at,
        branch_id,
        donar_id,
        organisation_id,
        payment_method_id,
        order_details (
          package:package_id (
            package_title
          )
        )
      `
      )
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (branchId) {
      ordersQuery = ordersQuery.eq('branch_id', branchId);
    }

    const { data, error } = await ordersQuery;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aid status for an order
export const updateOrderGiftAid = async (orderId, giftAidEligible) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .update({ gift_aid_eligible: giftAidEligible })
      .eq('id', orderId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR SPONSORSHIPS                               */
/* -------------------------------------------------------------------------- */

// Get sponsorships by donor ID
export const getDonorSponsorships = async donorId => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .select(
        `
        id,
        stipulation,
        theme,
        duration,
        renewal,
        created_at,
        start_date,
        updated_at,
        donar_id,
        orphan_id,
        orphan:orphan_id (
          id,
          country,
          first_name,
          gender,
          last_name,
          middle_name,
          image,
          DOB
        )
      `
      )
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR GIFT AID                                   */
/* -------------------------------------------------------------------------- */

// Get gift aid data for a donor (combines all gift aid queries)
export const getDonorGiftAid = async donorId => {
  try {
    // Get gift aid record
    const { data: giftAid, error: giftAidError } = await supabase
      .from('gift_aid')
      .select('id, amount')
      .eq('donar_id', donorId)
      .maybeSingle();

    if (giftAidError) return handleSupabaseError(giftAidError);

    // Get eligibility from most recent order
    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select('gift_aid_eligible')
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (ordersError) return handleSupabaseError(ordersError);

    // Get declaration log if gift aid exists
    let declarationLog = [];
    if (giftAid?.id) {
      const { data: logData, error: logError } = await supabase
        .from('declaration_log')
        .select('*')
        .eq('gift_aid_id', giftAid.id)
        .order('created_at', { ascending: false });

      if (logError) return handleSupabaseError(logError);
      declarationLog = logData || [];
    }

    return {
      data: {
        giftAidId: giftAid?.id || null,
        awaitingSubmission: giftAid?.amount || 0,
        eligible: orders?.[0]?.gift_aid_eligible || false,
        declarationLog
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR FEEDBACK/NOTES                             */
/* -------------------------------------------------------------------------- */

// Get all feedback/notes for a donor
export const getDonorFeedback = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor feedback/note
export const createDonorFeedback = async feedbackData => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .insert([feedbackData])
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor feedback/note
export const updateDonorFeedback = async (id, feedbackData) => {
  try {
    const { data, error } = await supabase
      .from('donars_feedback')
      .update(feedbackData)
      .eq('id', id)
      .select(
        `
        id,
        description,
        donar_id,
        priority,
        created_at,
        user:user_id (
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor feedback/note
export const deleteDonorFeedback = async id => {
  try {
    const { error } = await supabase
      .from('donars_feedback')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donors with donation totals for date range (for stats)
export const getDonorsWithOrdersByDate = async (
  organisationId,
  branchId,
  startDate,
  endDate
) => {
  try {
    console.log('getDonorsWithOrdersByDate called with:', {
      organisationId,
      branchId,
      startDate,
      endDate,
      branchIdType: typeof branchId
    });

    // Get donor count - simplified query
    let donorQuery = supabase
      .from('donars')
      .select('id', { count: 'exact' })
      .eq('organisation_id', organisationId);

    if (branchId) {
      donorQuery = donorQuery.eq('branch_id', branchId);
    }

    if (startDate && endDate) {
      donorQuery = donorQuery
        .gte('created_at', startDate)
        .lte('created_at', endDate);
    }

    const { count: donorCount, error: donorError } = await donorQuery;

    if (donorError) {
      console.error('Donor count query error:', {
        error: donorError,
        errorKeys: Object.keys(donorError),
        errorJSON: JSON.stringify(donorError),
        organisationId,
        branchId,
        startDate,
        endDate
      });
      return handleSupabaseError(donorError);
    }

    // Get orders for the date range
    let orderQuery = supabase
      .from('orders')
      .select('id, donation_amount, donar_id')
      .eq('organisation_id', organisationId);

    if (branchId) {
      orderQuery = orderQuery.eq('branch_id', branchId);
    }

    if (startDate && endDate) {
      orderQuery = orderQuery
        .gte('created_at', startDate)
        .lte('created_at', endDate);
    }

    const { data: orders, error: orderError } = await orderQuery;

    if (orderError) {
      console.error('Orders query error:', orderError);
      // Don't fail the whole query if orders fail, just return empty orders
      return {
        data: {
          donorCount: donorCount || 0,
          orders: []
        },
        error: null
      };
    }

    return {
      data: {
        donorCount: donorCount || 0,
        orders: orders || []
      },
      error: null
    };
  } catch (error) {
    console.error('getDonorsWithOrdersByDate error:', error);
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR CATEGORIES                                 */
/* -------------------------------------------------------------------------- */

// Get all donor categories (shared across all organizations/branches)
export const getDonorCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .select('*')
      .order('title', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor category
export const createDonorCategory = async ({ title, isDefault }) => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .insert([
        {
          title,
          is_default: isDefault
        }
      ])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor category
export const updateDonorCategory = async (id, { title, isDefault }) => {
  try {
    const { data, error } = await supabase
      .from('donor_category')
      .update({ title, is_default: isDefault })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor category
export const deleteDonorCategory = async id => {
  try {
    const { error } = await supabase
      .from('donor_category')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */

// Get all currencies
export const getCurrencies = async () => {
  try {
    const { data, error } = await supabase
      .from('currency')
      .select('*')
      .order('currency_name', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              LOCATIONS                                     */
/* -------------------------------------------------------------------------- */

// Get locations by organisation
export const getLocations = async (organisationId, branchId, type) => {
  try {
    let query = supabase
      .from('location')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    if (type) {
      query = query.eq('type', type);
    }

    const { data, error } = await query.order('location_name', {
      ascending: true
    });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                            ORGANISATION                                    */
/* -------------------------------------------------------------------------- */

// Every organisation column the browser may read. stripe_secret is
// deliberately absent: anon/authenticated have column-level SELECT that
// excludes it, and asking for it (or '*') fails the whole request.
const ORGANISATION_COLUMNS =
  'id, organisation_name, charity_number, city, post_code, country, ' +
  'organisation_email, logo, created_at, updated_at, currency_id, parent_id, ' +
  'address_1, address_2, stripe_key';

// Get organisation by ID
export const getOrganisation = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('organisation')
      .select(ORGANISATION_COLUMNS)
      .eq('id', organisationId)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update organisation
export const updateOrganisation = async (id, organisationData) => {
  try {
    const { data, error } = await supabase
      .from('organisation')
      .update(organisationData)
      .eq('id', id)
      .select(ORGANISATION_COLUMNS)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              USERS & ROLES                                 */
/*  -------------------------------------------------------------------------- */

// Get user roles for organization (org admin view)
export const getUserRoles = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get user roles for specific branch (branch admin view)
export const getBranchUserRoles = async (organisationId, branchId) => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('branch_id', branchId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single user role by ID
export const getUserRole = async id => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create user and user role via Edge Function
export const createUser = async ({
  email,
  password,
  firstName,
  lastName,
  organisationId,
  branchId,
  roleId,
  permissions
}) => {
  try {
    // Get the current session token
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (!session) {
      return handleSupabaseError({ message: 'Not authenticated' });
    }

    // Call the Edge Function to create user with admin privileges
    const { data, error } = await supabase.functions.invoke('create-user', {
      body: {
        email,
        password,
        firstName,
        lastName,
        organisationId,
        branchId,
        roleId,
        permissions
      }
    });

    if (error) return handleSupabaseError(error);
    if (data?.error) return handleSupabaseError({ message: data.error });

    return { data: data.data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update user role
export const updateUserRole = async (id, userRoleData) => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .update(userRoleData)
      .eq('id', id)
      .select(
        `
        *,
        user:user_id (
          id,
          first_name,
          last_name,
          email,
          email_verified_at
        ),
        role:role_id (
          id,
          name
        ),
        branch:branch_id (
          id,
          name
        )
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update user info (name, etc)
export const updateUser = async (id, userData) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(userData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete user role
export const deleteUserRole = async id => {
  try {
    const { error } = await supabase.from('users_roles').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              DASHBOARD                                     */
/* -------------------------------------------------------------------------- */

// Get donors count with optional filters
export const getDonorsCount = async (organisationId, branchId, dateFilter) => {
  try {
    let query = supabase
      .from('donars')
      .select('*', { count: 'exact', head: true })
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (dateFilter?.startDate && dateFilter?.endDate) {
      query = query
        .gte('created_at', dateFilter.startDate)
        .lte('created_at', dateFilter.endDate);
    }

    const { count, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data: count, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation amount sum with optional filters
export const getDonationAmount = async (
  organisationId,
  branchId,
  paymentStatus = 'paid'
) => {
  try {
    let query = supabase
      .from('orders')
      .select('donation_amount')
      .eq('organisation_id', organisationId)
      .eq('payment_status', paymentStatus);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Calculate sum manually
    const sum =
      data?.reduce((acc, order) => acc + (order.donation_amount || 0), 0) || 0;

    return { data: sum, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get recent orders with all details
export const getRecentOrders = async (organisationId, branchId, limit = 10) => {
  try {
    let query = supabase
      .from('orders')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email
        ),
        payment_method:payment_method_id (
          payment_name
        ),
        order_details (
          billing_condition,
          donation_type:donation_type_id (
            donation_type_name
          ),
          package:package_id (
            package_title
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'paid')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all orders with optional filters
export const getOrders = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('orders')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email
        ),
        payment_method:payment_method_id (
          payment_name
        ),
        order_details (
          billing_condition,
          amount,
          donation_type:donation_type_id (
            donation_type_name
          ),
          package:package_id (
            package_title
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('payment_status', 'paid')
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    // Apply filters if provided
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single order with details
export const getOrderDetails = async orderId => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select(
        `
        *,
        order_details (
          *,
          package:package_id (
            package_title,
            simple_id
          )
        )
      `
      )
      .eq('id', orderId)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donations for a specific donor (for sponsorship form)
export const getDonorDonations = async (donorId, organisationId) => {
  try {
    if (!donorId) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('orders')
      .select(
        `
        id,
        donation_amount,
        payment_status,
        payment_method:payment_method_id (
          payment_name
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation items (order details) for a specific donation/order
export const getDonationItems = async orderId => {
  try {
    if (!orderId) {
      return { data: [], error: null };
    }

    const { data, error } = await supabase
      .from('order_details')
      .select(
        `
        id,
        billing_condition,
        amount,
        donation_type:donation_type_id (
          donation_type_name
        )
      `
      )
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donor communications
export const getDonorCommunications = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donars_communication')
      .select('*')
      .eq('donar_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor communication
export const createDonorCommunication = async communicationData => {
  try {
    const { data, error } = await supabase
      .from('donars_communication')
      .insert([communicationData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         DONATION SETTINGS                                  */
/* -------------------------------------------------------------------------- */

// Get donation types
export const getDonationTypes = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('donation_type')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('donation_type_name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get packages with filters
export const getPackages = async (organisationId, branchId, filters = {}) => {
  try {
    // Fetch packages
    let query = supabase
      .from('packages')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('is_hidden', false)
      .eq('is_display', true);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('display_order', { ascending: true }).order('package_title');

    if (error) return handleSupabaseError(error);

    // Fetch related data separately and join in JS (FK joins not set up in DB)
    const { data: transactionFees } = await supabase
      .from('transaction_fee')
      .select('*')
      .eq('organisation_id', organisationId);

    const { data: currencies } = await supabase
      .from('currency')
      .select('*');

    // Join related data to packages
    const packagesWithRelations = data?.map(pkg => ({
      ...pkg,
      transaction_fee: pkg.transaction_fee_id
        ? transactionFees?.find(tf => tf.id === pkg.transaction_fee_id) || null
        : null,
      currency: pkg.currency_id
        ? currencies?.find(c => c.id === pkg.currency_id) || null
        : null
    }));

    // Apply JSONB filters in JavaScript since Supabase JSONB queries are complex
    let filteredData = packagesWithRelations;

    if (filters.paymentOption && filteredData) {
      filteredData = filteredData.filter(
        pkg =>
          pkg.payment_options &&
          pkg.payment_options.includes(filters.paymentOption)
      );
    }

    if (filters.donationTypeId && filteredData) {
      filteredData = filteredData.filter(
        pkg =>
          pkg.donation_type &&
          pkg.donation_type.includes(filters.donationTypeId)
      );
    }

    if (
      filters.locationIdList &&
      filters.locationIdList.length > 0 &&
      filteredData
    ) {
      filteredData = filteredData.filter(pkg => {
        if (!pkg.location) return false;
        return filters.locationIdList.some(locId =>
          pkg.location.includes(locId)
        );
      });
    }

    return { data: filteredData, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get transaction fees
export const getTransactionFees = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('transaction_fee')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get package categories
export const getPackageCategories = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('package_category')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('category_name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get campaigns
export const getCampaigns = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('campaign')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation amounts
export const getDonationAmounts = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('donation_amount')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('amount');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get donation currencies for an organization
export const getDonationCurrencies = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('donation_currency')
      .select(
        `
        *,
        donation_currency_organisation:organisation_id (
          currency:currency_id (
            id,
            abbreviation,
            currency_name,
            symbol
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get website settings for an organization/branch
export const getWebsiteSettings = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('website_settings')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      // Get settings for this branch OR org-wide settings (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    // Get the most specific settings (branch-level first, then org-level)
    const { data, error } = await query.order('branch_id', { ascending: false, nullsFirst: false }).limit(1).maybeSingle();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Resolve a campaign link (donate.example.org/<slug>) to the package and
// donation type it should preselect. RLS only exposes active rows to anon.
export const getDonationLinkBySlug = async (organisationId, slug, branchId) => {
  try {
    // ilike gives a case-insensitive match; its wildcards are escaped so a
    // crafted path segment can't match an arbitrary slug.
    const pattern = slug.replace(/[\\%_]/g, match => `\\${match}`);

    let query = supabase
      .from('donation_links')
      .select('id, slug, package_id, donation_type_id, branch_id')
      .eq('organisation_id', organisationId)
      .eq('is_active', true)
      .ilike('slug', pattern);

    if (branchId) {
      // Links for this branch OR org-wide links (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    // Branch-specific link first, then org-wide
    const { data, error } = await query
      .order('branch_id', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update website settings (upsert)
export const upsertWebsiteSettings = async settingsData => {
  try {
    const { data, error } = await supabase
      .from('website_settings')
      .upsert(settingsData, {
        onConflict: 'organisation_id,branch_id'
      })
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update organization
export const updateOrganization = async (organizationId, data) => {
  try {
    const { data: result, error } = await supabase
      .from('organisations')
      .update(data)
      .eq('id', organizationId)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           BENEFICIARIES/ORPHANS                            */
/* -------------------------------------------------------------------------- */

// Get all orphans/beneficiaries
export const getBeneficiaries = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('orphans')
      .select(
        `
        *,
        sponsorship_condition:sponsorship_condition_id (
          condition
        ),
        sponsorship_priority:sponsorship_priority_id (
          priority_type
        ),
        sponsorship_type:sponsorship_type_id (
          type
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('is_hidden', false)
      .order('created_at', { ascending: true });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single beneficiary/orphan
export const getBeneficiary = async id => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create beneficiary/orphan
export const createBeneficiary = async beneficiaryData => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .insert([beneficiaryData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update beneficiary/orphan
export const updateBeneficiary = async (id, beneficiaryData) => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .update(beneficiaryData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete beneficiary/orphan (soft delete)
export const deleteBeneficiary = async id => {
  try {
    const { data, error } = await supabase
      .from('orphans')
      .update({ is_hidden: true })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           SPONSORSHIPS                                     */
/* -------------------------------------------------------------------------- */

// Get all sponsorships
export const getSponsorships = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          email
        ),
        orphan:orphan_id (
          id,
          first_name,
          last_name,
          middle_name,
          country,
          image,
          DOB,
          gender,
          branch_id
        )
      `
      )
      .order('created_at', { ascending: false });

    // Filter by branch through orphan relationship
    if (branchId) {
      const { data: orphans } = await supabase
        .from('orphans')
        .select('id')
        .eq('branch_id', branchId);

      const orphanIds = orphans?.map(o => o.id) || [];
      if (orphanIds.length > 0) {
        query = query.in('orphan_id', orphanIds);
      } else {
        return { data: [], error: null };
      }
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single sponsorship
export const getSponsorship = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .select(
        `
        *,
        donar:donar_id (*),
        orphan:orphan_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship
export const createSponsorship = async sponsorshipData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .insert([sponsorshipData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship
export const updateSponsorship = async (id, sponsorshipData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .update(sponsorshipData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship
export const deleteSponsorship = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           GUARDIANS                                        */
/* -------------------------------------------------------------------------- */

// Create guardian
export const createGuardian = async guardianData => {
  try {
    const { data, error } = await supabase
      .from('guardian')
      .insert([guardianData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           SCHOOLS                                          */
/* -------------------------------------------------------------------------- */

// Get all schools
export const getSchools = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('schools')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single school
export const getSchool = async id => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school
export const createSchool = async schoolData => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .insert([schoolData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school
export const updateSchool = async (id, schoolData) => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .update(schoolData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school
export const deleteSchool = async id => {
  try {
    const { data, error } = await supabase
      .from('schools')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single school student by ID
export const getSchoolStudent = async id => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .select(
        `
        *,
        school:school_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get school students for a beneficiary
export const getSchoolStudents = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .select(
        `
        *,
        school:school_id (*)
      `
      )
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school student
export const createSchoolStudent = async studentData => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .insert([studentData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school student
export const updateSchoolStudent = async (id, studentData) => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .update(studentData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school student
export const deleteSchoolStudent = async id => {
  try {
    const { data, error } = await supabase
      .from('school_students')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get school reports
export const getSchoolReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('school_report')
      .select('*')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create school report
export const createSchoolReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('school_report')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update school report
export const updateSchoolReport = async (id, reportData) => {
  try {
    const { data, error } = await supabase
      .from('school_report')
      .update(reportData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete school report
export const deleteSchoolReport = async id => {
  try {
    const { data, error } = await supabase
      .from('school_report')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           HOSPITALS                                        */
/* -------------------------------------------------------------------------- */

// Get all hospitals
export const getHospitals = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('hospitals')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single hospital
export const getHospital = async id => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create hospital
export const createHospital = async hospitalData => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .insert([hospitalData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update hospital
export const updateHospital = async (id, hospitalData) => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .update(hospitalData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete hospital
export const deleteHospital = async id => {
  try {
    const { data, error } = await supabase
      .from('hospitals')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           MOSQUES                                          */
/* -------------------------------------------------------------------------- */

// Get all mosques
export const getMosques = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('mosques')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single mosque
export const getMosque = async id => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create mosque
export const createMosque = async mosqueData => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .insert([mosqueData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update mosque
export const updateMosque = async (id, mosqueData) => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .update(mosqueData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete mosque
export const deleteMosque = async id => {
  try {
    const { data, error } = await supabase
      .from('mosques')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           ORPHANAGES                                       */
/* -------------------------------------------------------------------------- */

// Get all orphanages
export const getOrphanages = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('orphanages')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single orphanage
export const getOrphanage = async id => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create orphanage
export const createOrphanage = async orphanageData => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .insert([orphanageData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update orphanage
export const updateOrphanage = async (id, orphanageData) => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .update(orphanageData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete orphanage
export const deleteOrphanage = async id => {
  try {
    const { data, error } = await supabase
      .from('orphanages')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           PACKAGES                                         */
/* -------------------------------------------------------------------------- */

// Get single package by ID
export const getPackage = async id => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create package
export const createPackage = async packageData => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .insert([packageData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update package
export const updatePackage = async (id, packageData) => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .update(packageData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete package
export const deletePackage = async id => {
  try {
    const { data, error } = await supabase
      .from('packages')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         URGENT APPEALS                                     */
/* -------------------------------------------------------------------------- */

// Get all urgent appeals
export const getUrgentAppeals = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('urgent_appeals')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get urgent appeals with package details (for public donation page)
export const getUrgentAppealsWithPackages = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('urgent_appeals')
      .select(
        `
        *,
        package:package_id (
          currency_id,
          donation_amount,
          donation_amounts,
          donation_type,
          donation_types,
          location,
          id,
          simple_id,
          organisation_id,
          package_category,
          package_description,
          package_title,
          payment_options,
          redirect_url,
          status,
          transaction_fee_id,
          branch_id,
          display_order
        )
      `
      )
      .eq('organisation_id', organisationId)
      .eq('is_display', true);

    if (branchId) {
      // Get items for this branch OR org-wide items (branch_id IS NULL)
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Sort by urgent appeal display_order, then by created_at as fallback
    const sorted = data?.sort((a, b) => {
      const orderA = a.display_order ?? 999;
      const orderB = b.display_order ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return { data: sorted, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single urgent appeal
export const getUrgentAppeal = async id => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create urgent appeal
export const createUrgentAppeal = async appealData => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .insert([appealData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update urgent appeal
export const updateUrgentAppeal = async (id, appealData) => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .update(appealData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete urgent appeal
export const deleteUrgentAppeal = async id => {
  try {
    const { data, error } = await supabase
      .from('urgent_appeals')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           GIFT AID                                         */
/* -------------------------------------------------------------------------- */

// Get all gift aids
export const getGiftAids = async (organisationId, branchId, filters = {}) => {
  try {
    let query = supabase
      .from('gift_aid')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name
        ),
        order:order_id (
          id,
          simple_id,
          donation_amount
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get single gift aid
export const getGiftAid = async id => {
  try {
    const { data, error } = await supabase
      .from('gift_aid')
      .select(
        `
        *,
        donar:donar_id (*),
        order:order_id (*)
      `
      )
      .eq('id', id)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aids with donar address for unclaimed transactions
export const getGiftAidsWithAddress = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('gift_aid')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          title,
          address
        ),
        order:order_id (
          donation_amount
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid aggregate sum
export const getGiftAidAggregateSum = async (
  organisationId,
  branchId,
  filters = {}
) => {
  try {
    let query = supabase
      .from('gift_aid')
      .select('amount')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    const sum = data?.reduce((acc, item) => acc + (item.amount || 0), 0) || 0;
    return { data: { sum }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get all gift aid applications
export const getGiftAidApplications = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('gift_aid_applications')
      .select(
        `
        *,
        gift_aids:gift_aids(
          id,
          amount,
          order:order_id (
            donation_amount
          )
        )
      `
      )
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid application aggregates
export const getGiftAidApplicationAggregates = async (
  organisationId,
  branchId
) => {
  try {
    let query = supabase
      .from('gift_aid_applications')
      .select('status')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.eq('branch_id', branchId);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);

    // Count by status
    const completedCount =
      data?.filter(app => app.status === 'completed').length || 0;
    const submittedCount =
      data?.filter(app => app.status === 'submitted').length || 0;
    const awaitingCount =
      data?.filter(app => app.status === 'awaiting').length || 0;

    return {
      data: {
        completedCount,
        submittedCount,
        awaitingCount
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aids by application ID
export const getGiftAidsByApplicationId = async applicationId => {
  try {
    const { data, error } = await supabase
      .from('gift_aid')
      .select(
        `
        *,
        donar:donar_id (
          id,
          first_name,
          last_name,
          title,
          address
        ),
        order:order_id (
          donation_amount
        )
      `
      )
      .eq('gift_aid_application_id', applicationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gift aid aggregate by application ID
export const getGiftAidAggregateByApplicationId = async applicationId => {
  try {
    const { data, error } = await supabase
      .from('gift_aid')
      .select('amount, status, order:order_id (donation_amount)')
      .eq('gift_aid_application_id', applicationId);

    if (error) return handleSupabaseError(error);

    const sum = data?.reduce((acc, item) => acc + (item.amount || 0), 0) || 0;
    const count = data?.length || 0;
    const acceptedCount =
      data?.filter(item => item.status === 'accepted').length || 0;
    const rejectedCount =
      data?.filter(item => item.status === 'rejected').length || 0;

    return {
      data: {
        sum,
        count,
        acceptedCount,
        rejectedCount,
        nodes: data
      },
      error: null
    };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create gift aid application
export const createGiftAidApplication = async applicationData => {
  try {
    const { data, error } = await supabase
      .from('gift_aid_applications')
      .insert(applicationData)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aids status
export const updateGiftAidsStatus = async (
  giftAidIds,
  status,
  applicationId
) => {
  try {
    const updateData = {
      status,
      gift_aid_application_id: applicationId
    };

    const { data, error } = await supabase
      .from('gift_aid')
      .update(updateData)
      .in('id', giftAidIds)
      .select();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update gift aid application status
export const updateGiftAidApplicationStatus = async (
  id,
  status,
  submittedDate
) => {
  try {
    const updateData = {
      status,
      ...(submittedDate && { submitted_date: submittedDate })
    };

    const { data, error } = await supabase
      .from('gift_aid_applications')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update single gift aid status
export const updateGiftAidStatus = async (id, status) => {
  try {
    const { data, error } = await supabase
      .from('gift_aid')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    INSTITUTION-BENEFICIARY LINKS                           */
/* -------------------------------------------------------------------------- */

// Get hospital orphans (beneficiaries linked to hospitals)
export const getHospitalOrphans = async hospitalId => {
  try {
    const { data, error } = await supabase
      .from('hospital_orphan')
      .select(
        `
        *,
        orphan:orphan_id (*),
        hospital:hospital_id (*)
      `
      )
      .eq('hospital_id', hospitalId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create hospital orphan link
export const createHospitalOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('hospital_orphan')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete hospital orphan link
export const deleteHospitalOrphan = async id => {
  try {
    const { error } = await supabase
      .from('hospital_orphan')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get mosque orphans
export const getMosqueOrphans = async mosqueId => {
  try {
    const { data, error } = await supabase
      .from('mosque_orphan')
      .select(
        `
        *,
        orphan:orphan_id (*),
        mosque:mosque_id (*)
      `
      )
      .eq('mosque_id', mosqueId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create mosque orphan link
export const createMosqueOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('mosque_orphan')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete mosque orphan link
export const deleteMosqueOrphan = async id => {
  try {
    const { error } = await supabase
      .from('mosque_orphan')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get orphanage orphans
export const getOrphanageOrphans = async orphanageId => {
  try {
    const { data, error } = await supabase
      .from('orphan_orphanage')
      .select(
        `
        *,
        orphan:orphan_id (*),
        orphanage:orphanage_id (*)
      `
      )
      .eq('orphanage_id', orphanageId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create orphanage orphan link
export const createOrphanageOrphan = async data => {
  try {
    const { data: result, error } = await supabase
      .from('orphan_orphanage')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete orphanage orphan link
export const deleteOrphanageOrphan = async id => {
  try {
    const { error } = await supabase
      .from('orphan_orphanage')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    MEDICAL REPORTS & GALLERY                               */
/* -------------------------------------------------------------------------- */

// Get medical reports for beneficiary
export const getMedicalReports = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('medical_reports')
      .select('*')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create medical report
export const createMedicalReport = async reportData => {
  try {
    const { data, error } = await supabase
      .from('medical_reports')
      .insert([reportData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get gallery items for beneficiary
export const getGalleryItems = async beneficiaryId => {
  try {
    const { data, error } = await supabase
      .from('gallery')
      .select('*')
      .eq('orphan_id', beneficiaryId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create gallery item
export const createGalleryItem = async itemData => {
  try {
    const { data, error } = await supabase
      .from('gallery')
      .insert([itemData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    SPONSORSHIP SETTINGS                                    */
/* -------------------------------------------------------------------------- */

// Get sponsorship types
export const getSponsorshipTypes = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_type')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('type');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship conditions
export const getSponsorshipConditions = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_condition')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('condition');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship costs
export const getSponsorshipCosts = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_cost')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('amount');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship priorities
export const getSponsorshipPriorities = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_priority')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('priority_type');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship durations
export const getSponsorshipDurations = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_duration')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('duration');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get sponsorship statuses
export const getSponsorshipStatuses = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('sponsorship_status')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query.order('status');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship priority
export const createSponsorshipPriority = async priorityData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .insert([priorityData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship priority
export const updateSponsorshipPriority = async (id, priorityData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .update(priorityData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship priority
export const deleteSponsorshipPriority = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_priority')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create sponsorship status
export const createSponsorshipStatus = async statusData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .insert([statusData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update sponsorship status
export const updateSponsorshipStatus = async (id, statusData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .update(statusData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete sponsorship status
export const deleteSponsorshipStatus = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_status')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Types
export const createSponsorshipType = async typeData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .insert([typeData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipType = async (id, typeData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .update(typeData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipType = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_type')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Conditions
export const createSponsorshipCondition = async conditionData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .insert([conditionData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipCondition = async (id, conditionData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .update(conditionData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipCondition = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_condition')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Costs
export const createSponsorshipCost = async costData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .insert([costData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipCost = async (id, costData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .update(costData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipCost = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_cost')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// CRUD for Sponsorship Durations
export const createSponsorshipDuration = async durationData => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .insert([durationData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateSponsorshipDuration = async (id, durationData) => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .update(durationData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteSponsorshipDuration = async id => {
  try {
    const { data, error } = await supabase
      .from('sponsorship_duration')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Contact table (for institutions)
export const getContacts = async (organisationId, branchId) => {
  try {
    let query = supabase
      .from('contact')
      .select('*')
      .eq('organisation_id', organisationId);

    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }

    const { data, error } = await query;

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                        GUARDIAN & LOCATION OPTIONS                         */
/* -------------------------------------------------------------------------- */

// Get guardian father status options
export const getGuardianFatherStatus = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_father_status')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get guardian mother status options
export const getGuardianMotherStatus = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_mother_status')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get guardian relationship options
export const getGuardianRelationship = async () => {
  try {
    const { data, error } = await supabase
      .from('guardian_relationShip')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get location condition options
export const getLocationConditions = async () => {
  try {
    const { data, error } = await supabase
      .from('location_condition')
      .select('*')
      .order('name');

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get shortcuts
export const getShortcuts = async organisationId => {
  try {
    const { data, error } = await supabase
      .from('shortcuts')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create shortcut
export const createShortcut = async shortcutData => {
  try {
    const { data, error } = await supabase
      .from('shortcuts')
      .insert([shortcutData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR TASKS                                      */
/* -------------------------------------------------------------------------- */

// Get donor tasks
export const getDonorTasks = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .select('*')
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor task
export const createDonorTask = async taskData => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .insert([taskData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor task
export const updateDonorTask = async (id, taskData) => {
  try {
    const { data, error } = await supabase
      .from('donor_tasks')
      .update(taskData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor task
export const deleteDonorTask = async id => {
  try {
    const { error } = await supabase.from('donor_tasks').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { data: { id }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           DONOR COMMENTS                                   */
/* -------------------------------------------------------------------------- */

// Get donor comments
export const getDonorComments = async donorId => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .eq('donor_id', donorId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Create donor comment
export const createDonorComment = async commentData => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .insert([commentData])
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor comment
export const updateDonorComment = async (id, commentData) => {
  try {
    const { data, error } = await supabase
      .from('donor_comments')
      .update(commentData)
      .eq('id', id)
      .select(
        `
        *,
        user:users!reporter_id(first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete donor comments (multiple)
export const deleteDonorComments = async ids => {
  try {
    const { error } = await supabase
      .from('donor_comments')
      .delete()
      .in('id', ids);

    if (error) return handleSupabaseError(error);
    return { data: { ids }, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                           STAFF (USERS)                                    */
/* -------------------------------------------------------------------------- */

// Get branch staff
export const getBranchStaff = async branchId => {
  try {
    const { data, error } = await supabase
      .from('users_roles')
      .select(
        `
        *,
        user:users!user_id(id, first_name, last_name, email)
      `
      )
      .eq('branch_id', branchId)
      .eq('is_active', true);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update donor assigned staff
export const updateDonorAssignedStaff = async (donorId, staffId) => {
  try {
    const { data, error } = await supabase
      .from('donars')
      .update({ user_id: staffId })
      .eq('id', donorId)
      .select(
        `
        *,
        user:users!user_id(id, first_name, last_name)
      `
      )
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get top donations for donor
export const getTopDonations = async (donorId, limit = 3) => {
  try {
    const { data, error } = await supabase
      .from('order_details')
      .select(
        `
        id,
        amount,
        package:packages!inner(package_title)
      `
      )
      .eq('order.donar_id', donorId)
      .order('amount', { ascending: false })
      .limit(limit);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              LOGS                                          */
/* -------------------------------------------------------------------------- */

// Get weraise module by name
export const getWeraiseModule = async moduleName => {
  try {
    const { data, error } = await supabase
      .from('weraise_module')
      .select('*')
      .eq('name', moduleName)
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get logs for a module
export const getLogs = async (organisationId, moduleId, moduleReferenceId) => {
  try {
    const { data, error } = await supabase
      .from('all_logs')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('weraise_module_id', moduleId)
      .eq('module_reference_id', moduleReferenceId)
      .order('created_at', { ascending: false });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Insert log
export const insertLog = async logData => {
  try {
    const { data, error } = await supabase
      .from('all_logs')
      .insert([logData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                              CALENDAR EVENTS                               */
/* -------------------------------------------------------------------------- */

// Get calendar events
export const getCalendarEvents = async (organisationId, branchId) => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('branch_id', branchId)
      .order('start_date', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Insert calendar event
export const insertCalendarEvent = async eventData => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([eventData])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Update calendar event
export const updateCalendarEvent = async (id, eventData) => {
  try {
    const { data, error } = await supabase
      .from('calendar_events')
      .update(eventData)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Delete calendar event
export const deleteCalendarEvent = async id => {
  try {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                    DONATION SETTINGS MUTATIONS                             */
/* -------------------------------------------------------------------------- */

// Donation Type CRUD
export const createDonationType = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_type')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationType = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_type')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationType = async id => {
  try {
    const { error } = await supabase
      .from('donation_type')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Location CRUD
export const createLocation = async data => {
  try {
    const { data: result, error } = await supabase
      .from('location')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateLocation = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('location')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteLocation = async id => {
  try {
    const { error } = await supabase.from('location').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Campaign CRUD
export const createCampaign = async data => {
  try {
    const { data: result, error } = await supabase
      .from('campaign')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateCampaign = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('campaign')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteCampaign = async id => {
  try {
    const { error } = await supabase.from('campaign').delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Transaction Fee CRUD
export const createTransactionFee = async data => {
  try {
    const { data: result, error } = await supabase
      .from('transaction_fee')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateTransactionFee = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('transaction_fee')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteTransactionFee = async id => {
  try {
    const { error } = await supabase
      .from('transaction_fee')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Donation Amount CRUD
export const createDonationAmount = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_amount')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationAmount = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_amount')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationAmount = async id => {
  try {
    const { error } = await supabase
      .from('donation_amount')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Package Category CRUD
export const createPackageCategory = async data => {
  try {
    const { data: result, error } = await supabase
      .from('package_category')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updatePackageCategory = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('package_category')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deletePackageCategory = async id => {
  try {
    const { error } = await supabase
      .from('package_category')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Donation Currency CRUD
export const createDonationCurrency = async data => {
  try {
    const { data: result, error } = await supabase
      .from('donation_currency')
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const updateDonationCurrency = async (id, data) => {
  try {
    const { data: result, error } = await supabase
      .from('donation_currency')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

export const deleteDonationCurrency = async id => {
  try {
    const { error } = await supabase
      .from('donation_currency')
      .delete()
      .eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

/* -------------------------------------------------------------------------- */
/*                         GENERIC CRUD HELPERS                               */
/* -------------------------------------------------------------------------- */

// Generic get all for any table
export const getAll = async (tableName, organisationId) => {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('id, name')
      .eq('organisation_id', organisationId)
      .order('name', { ascending: true });

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic create for any table with name field
export const createOption = async (tableName, data) => {
  try {
    const { data: result, error } = await supabase
      .from(tableName)
      .insert([data])
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic update for any table
export const updateOption = async (tableName, id, data) => {
  try {
    const { data: result, error } = await supabase
      .from(tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) return handleSupabaseError(error);
    return { data: result, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Generic delete for any table
export const deleteOption = async (tableName, id) => {
  try {
    const { error } = await supabase.from(tableName).delete().eq('id', id);

    if (error) return handleSupabaseError(error);
    return { error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};

// Get contact by mosque ID
export const getContactByMosqueId = async mosqueId => {
  try {
    const { data, error } = await supabase
      .from('contact')
      .select('*')
      .eq('mosque_id', mosqueId);

    if (error) return handleSupabaseError(error);
    return { data, error: null };
  } catch (error) {
    return handleSupabaseError(error);
  }
};


export const persistBasketSession = async ({
  donorId,
  basket,
  transactionFee,
  subtotal,
  totalAmount,
  branchId,
  organisationId,
  giftAidEligible,
  giftAidData,
  orderDetails,
  paymentIntentId
}) => {
  try {
    const basketJson = typeof basket === 'string' ? JSON.parse(basket) : basket;
    console.log('Creating basket session:', { donorId, subtotal, totalAmount, branchId });

    // Call edge function to create/update basket session
    const { data, error } = await supabase.functions.invoke('create-basket-session', {
      body: {
        basket: basketJson,
        donorId,
        subtotal,
        transactionFee,
        totalAmount,
        branchId,
        organisationId,
        giftAidEligible,
        giftAidData,
        orderDetails,
        paymentIntentId
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      return {
        data: null,
        error: error.message || 'Failed to create basket session'
      };
    }

    if (data?.error) {
      return {
        data: null,
        error: data.error
      };
    }

    return {
      data: data.data,
      error: null
    };
  } catch (error) {
    console.error('Create basket session error:', error);
    return {
      data: null,
      error: error.message || 'Failed to create basket session'
    };
  }
}


export const recoverPaymentSummary = async (sessionId, branchId) => {
try {
    // Call edge function which creates donor and payment intent
    const { data, error } = await supabase.functions.invoke('recover-payment-summary', {
      body: {
        sessionId: sessionId,
        branchId: branchId,
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      return {
        data: null,
        error: error.message || 'Failed to create payment intent'
      };
    }

    if (data?.error) {
      return {
        data: null,
        error: data.error
      };
    }

    return {
      data: {
        basket: data.data?.basket,
        publishable_key: data.data?.publishable_key,
        client_secret: data.data?.client_secret,
        subtotal: data.data?.subtotal,
        transactionFee: data.data?.transactionFee,
        totalAmount: data.data?.totalAmount,
        giftAidEligible: data.data?.giftAidEligible,
        donorId: data.data?.donorId
      },
      error: null
    };
  } catch (error) {
    console.error('Create payment intent error:', error);
    return {
      data: null,
      error: error.message || 'Failed to create payment intent'
    };
  }
}